import { EmailParams } from '../types/email';
import { API_BASE_URL } from "../config/api";
import { fetchWithActor } from "../api/actor";
import { SendOutcomeUnknownError } from "./sendOutcome";
import type { ErrorLike } from "../utils/errors";

const SEND_TIMEOUT_MS = 60000;
/** Bad Gateway / Gateway Timeout: the proxy's answer, not the backend's. */
const GATEWAY_STATUSES = new Set([502, 504]);

/**
 * Send an email using the backend email service
 * @param params Email parameters including optional threading headers
 * @returns Promise<boolean> Success status
 * @throws SendOutcomeUnknownError when the email may have gone out anyway
 *   (timeout, dropped connection, gateway error, unreadable 2xx body); a
 *   plain Error when the backend reported that the send failed.
 */
export const sendEmail = async (params: EmailParams): Promise<boolean> => {
  try {
    // Clean up threading headers if present
    if (params.inReplyTo) {
      params.inReplyTo = params.inReplyTo.trim().replace(/[<>]/g, '');
    }
    if (params.references) {
      params.references = params.references.trim().split(/\s+/).map(ref => ref.replace(/[<>]/g, '')).join(' ');
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);

    try {
      let response: Response;
      try {
        response = await fetchWithActor(`${API_BASE_URL}/email/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
          signal: controller.signal
        });
      } catch (fetchError) {
        // The request left the browser and no answer came back. The backend
        // replies only after the send, the Sent-folder save, the inbox move
        // and the portal hooks, so the email may well have gone out.
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          throw new SendOutcomeUnknownError(`Email sending timed out after ${SEND_TIMEOUT_MS / 1000} seconds`);
        }
        const detail = fetchError instanceof Error ? fetchError.message : String(fetchError);
        throw new SendOutcomeUnknownError(`Network error while sending email: ${detail}`);
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Handle both JSON and HTML error responses
        let errorMessage = `Failed to send email: ${response.status}`;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
          } else {
            // If it's HTML or other content type, use the status text
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
            console.error('Received non-JSON response:', contentType);
          }
        } catch (parseError) {
          // If we can't parse the response, use the status
          console.error('Failed to parse error response:', parseError);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        // A gateway error is the proxy giving up on the backend, not the
        // backend reporting a failed send - the send may still have completed.
        if (GATEWAY_STATUSES.has(response.status)) {
          throw new SendOutcomeUnknownError(errorMessage);
        }
        throw new Error(errorMessage);
      }

      try {
        const data = await response.json();
        return data.success;
      } catch {
        // A 2xx means the backend finished the send; only its reply is bad.
        throw new SendOutcomeUnknownError('The server accepted the email but its reply could not be read');
      }
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to send email');
  }
};

/**
 * Check for new emails
 * @returns Promise<{ success: boolean; processing?: number; processed?: number; message: string }> Check result
 */
export const checkEmails = async (): Promise<{ success: boolean; processing?: number; processed?: number; message: string }> => {
  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

    const response = await fetchWithActor(`${API_BASE_URL}/check-emails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('Invalid content type:', contentType);
      throw new Error(`Expected JSON response but got ${contentType}`);
    }

    if (!response.ok) {
      const error = await response.json();
      console.error('Error response:', error);
      throw new Error(error.message || `Failed to check emails: ${response.status}`);
    }

    const result = await response.json();
    
    // Return standardized response
    return {
      success: result.success,
      processing: result.processing,
      processed: result.processed,
      message: result.message || 'Email check completed successfully'
    };
  } catch (caught) {
    const error = caught as ErrorLike;
    console.error('Error checking emails:', error);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 2 minutes');
    }
    throw error; // Throw the original error to preserve the message
  }
};
